import { useEffect } from "react";
import { Link } from "react-router-dom";

const StoriesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('in');
          observer.unobserve(e.target);
        }
      });
    }, { threshold: 0.1 });
    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const testimonials = [
    { area: "Career", text: "For years I could not understand why my career kept hitting the same wall despite real effort. The analysis was the first time anyone explained what was actually happening — without fear, without pressure. Within months of the Navagraha Yagya, things finally began to move.", name: "Rahul M.", loc: "Pune, Maharashtra", initial: "R" },
    { area: "Relationship", text: "What struck me most was that nobody tried to frighten me. The astrologer explained the chart, told me what she saw, and left the decision entirely with me. The bracelet arrived beautifully presented with every stone explained in writing.", name: "Priya S.", loc: "Bengaluru, Karnataka", initial: "P" },
    { area: "Sceptic Converted", text: "I was deeply sceptical of online astrology. Amatya changed that. I received the full video of the Yagya — I heard my own name and Gotra spoken clearly. It gave me a peace I had not felt in years.", name: "Arjun K.", loc: "Delhi, NCR", initial: "A" },
    { area: "Financial", text: "I had been to three other astrologers who all told me something different and wanted more and more money. Amatya told me one clear thing, performed one specific Daan on my behalf, and sent me the photos. No upsell. No drama. Just honesty.", name: "Suresh V.", loc: "Mumbai, Maharashtra", initial: "S" },
    { area: "Family", text: "My mother and I had not spoken properly in two years. The analysis identified a specific ancestral pattern. The Pitru Paksha Yagya was suggested, explained fully, and performed. Three months later, she called me. I do not know how to explain it, but something shifted.", name: "Meera L.", loc: "Chennai, Tamil Nadu", initial: "M" },
    { area: "Marriage", text: "After being told for years that my Mangal Dosha was catastrophic, Amatya calmly explained it in context, showed me it was manageable, and prescribed a specific and proportional remedy. No panic, no enormous bill. Just care.", name: "Kavya R.", loc: "Hyderabad, Telangana", initial: "K" }
  ];

  const stats = [
    { num: "2,400+", label: "Clients Served" },
    { num: "98%", label: "Satisfaction Rate" },
    { num: "100%", label: "Rituals Documented" },
    { num: "0", label: "Fear-Based Predictions" }
  ];

  return (
    <div data-testid="stories-page">
      {/* Hero */}
      <div className="page-hero" data-testid="stories-hero">
        <div className="page-hero-content">
          <span className="page-hero-label">Client Stories</span>
          <h1 className="page-hero-title">Words from those<br/>we have <em>served.</em></h1>
          <p className="page-hero-sub">These are real experiences from real people. Unembellished, unexaggerated, and shared with permission. The measure of our work is the clarity our clients find.</p>
        </div>
      </div>

      {/* Testimonials Grid */}
      <section className="testimonials-page" data-testid="testimonials-grid">
        <div className="wrap">
          <div className="test-full-grid">
            {testimonials.map((t, i) => (
              <div key={i} className={`test-full-card reveal ${i > 0 ? `d${Math.min(i, 2)}` : ''}`} data-testid={`testimonial-${i}`}>
                <div className="tfc-quote">"</div>
                <div className="tfc-area">{t.area}</div>
                <p className="tfc-text">{t.text}</p>
                <div className="tfc-author">
                  <div className="tfc-avatar">{t.initial}</div>
                  <div>
                    <span className="tfc-name">{t.name}</span>
                    <span className="tfc-loc">{t.loc}</span>
                    <span className="tfc-stars">★ ★ ★ ★ ★</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Results Banner */}
          <div className="results-banner reveal" data-testid="results-banner">
            <span className="section-label" style={{display:'block',marginBottom:'16px'}}>By the Numbers</span>
            <h2 className="section-title" style={{color:'var(--ember)'}}>The measure of our care</h2>
            <div className="results-grid">
              {stats.map((s, i) => (
                <div key={i} className="result-stat" data-testid={`stat-${i}`}>
                  <span className="rs-num">{s.num}</span>
                  <span className="rs-label">{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <div className="bottom-cta" data-testid="stories-cta">
        <div style={{position:'relative',zIndex:2}} className="reveal">
          <span className="bc-dev">॥ ॐ गं गणपतये नमः ॥</span>
          <h2 className="bc-title">Your story could begin <em>here.</em></h2>
          <p className="bc-body">One honest conversation with your birth chart. Explained clearly. Remedies offered only when warranted.</p>
          <div className="bc-btns">
            <a
              href="https://wa.me/917633997555?text=Hi%20Amatya%2C%20I%20would%20like%20to%20book%20my%20Astro%20Analysis%20reading.%20Please%20share%20the%20next%20steps."
              target="_blank"
              rel="noopener noreferrer"
              data-testid="stories-whatsapp-book-btn"
              className="btn"
              style={{background:'linear-gradient(135deg, #25D366 0%, #128C7E 100%)', color:'#fff', border:'none', display:'inline-flex', alignItems:'center', gap:'8px', boxShadow:'0 6px 20px rgba(18, 140, 126, 0.28)'}}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.464 3.488"/></svg>
              Book Your Reading Now!!
            </a>
            <Link to="/begin-journey" className="btn btn-ember">What's Included in Your Consultation <span className="btn-arr">→</span></Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoriesPage;
